import * as r from 'ramda';
import { put, call, select, takeLeading } from 'redux-saga/effects';
import { Toaster, Intent, Position } from '@blueprintjs/core';

import * as $ui from 'modules/ui';
import { UI } from 'modules/ui';
import * as $file from 'modules/file';
import * as $auth from 'modules/auth';
import * as api from '../api';
import * as actions from '../actions';
import * as selectors from '../selectors';

const WidgetsSettingsToaster = Toaster.create({
  position: Position.BOTTOM_RIGHT,
});

const uploadLogoImage = function* (logoImage) {
  const token = yield select($auth.getToken);
  const publisherId = yield select($auth.getSelectedPublisherId);

  const logoFile = yield call($file.createFileFromImageUrl, logoImage.url);
  logoFile.name = logoImage.name;

  const logoImageUrl = yield call($file.uploadImage, logoFile, {
    token,
    publisherId,
  });

  return logoImageUrl;
};

const getLogoImageUploadUrl = function* (logoImage) {
  if (logoImage.url.startsWith('blob:')) {
    const result = yield call(uploadLogoImage, logoImage);
    URL.revokeObjectURL(logoImage.url);
    return result;
  }

  return logoImage.url;
};

const updateWidgetsSettings = function* (widgetsSettings) {
  const { title, description, logoImage, mainColor, backgroundColor, fontFamily } = widgetsSettings;

  let logoImageUrl = null;
  if (logoImage) {
    logoImageUrl = yield call(getLogoImageUploadUrl, logoImage);
  }

  const widgetsSettingsData = {
    CustomSettings: {
      MainColor: mainColor,
      BackgroundColor: backgroundColor,
      FontFamily: fontFamily,
    },
    PromotionLayouts: {
      Title: title,
      Description: description,
      LogoUrl: logoImageUrl,
    },
  };

  const { Data: currentUserProfileData } = yield select(selectors.getUserProfileRaw);
  const profile = r.mergeDeepRight(currentUserProfileData, widgetsSettingsData);

  const token = yield select($auth.getToken);
  const publisherId = yield select($auth.getSelectedPublisherId);
  const { id: userId } = yield select($auth.getCurrentUser);
  const userProfileId = yield select(selectors.getUserProfileId);

  const updatedUserProfile = yield call(
    api.updateUserProfile,
    { userId, userProfileId, profile },
    {
      token,
      publisherId,
    }
  );
  yield put(actions.updateUserProfileSuccess(updatedUserProfile));
};

const updateWidgetsSettingsRequest = function* ({ payload: widgetsSettingsData }) {
  yield put($ui.setTrue(UI.UserProfile.IsWidgetsSettingsUpdating)());

  try {
    yield call(updateWidgetsSettings, widgetsSettingsData);

    WidgetsSettingsToaster.show({
      icon: 'tick',
      intent: Intent.SUCCESS,
      message: 'Successfully saved',
    });
  } catch {
    WidgetsSettingsToaster.show({
      icon: 'warning-sign',
      intent: Intent.DANGER,
      message: 'Something went wrong. Please try again later or contact support',
    });
  } finally {
    yield put($ui.setFalse(UI.UserProfile.IsWidgetsSettingsUpdating)());
  }
};

export const updateWidgetsSettingsSaga = function* () {
  yield takeLeading(actions.updateWidgetsSettings, updateWidgetsSettingsRequest);
};
